import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Project } from "../data/projects";

const STATUS_LABEL: Record<Project["status"], string> = {
  live: "live",
  beta: "beta",
  building: "building",
  dormant: "dormant",
};

const STATUS_COLOR: Record<Project["status"], string> = {
  live: "var(--status-live)",
  beta: "var(--status-beta)",
  building: "var(--status-building)",
  dormant: "var(--status-dormant)",
};

interface Props {
  project: Project | null;
  onClose: () => void;
}

export default function ProjectPanel({ project, onClose }: Props) {
  useEffect(() => {
    if (!project) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [project, onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        background: project ? "rgba(0, 0, 0, 0.55)" : "rgba(0, 0, 0, 0)",
        backdropFilter: project ? "blur(6px)" : "blur(0px)",
        WebkitBackdropFilter: project ? "blur(6px)" : "blur(0px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: project ? 1 : 0,
        pointerEvents: project ? "auto" : "none",
        transition: "opacity 0.25s ease, backdrop-filter 0.25s ease",
        zIndex: 10,
        padding: "1.5rem",
      }}
      aria-hidden={!project}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
        style={{
          width: "min(520px, 100%)",
          maxHeight: "85vh",
          overflowY: "auto",
          background: "rgba(10, 10, 15, 0.96)",
          border: "1px solid rgba(217, 119, 87, 0.35)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          padding: "2rem 2rem 1.75rem",
          transform: project ? "scale(1) translateY(0)" : "scale(0.96) translateY(8px)",
          transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {project && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "1.25rem",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: "0.7rem",
                  color: STATUS_COLOR[project.status],
                  background: "rgba(255,255,255,0.04)",
                  padding: "0.25rem 0.55rem",
                  border: `1px solid ${STATUS_COLOR[project.status]}55`,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {STATUS_LABEL[project.status]}
              </span>
              <button
                onClick={onClose}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--fg-dim)",
                  fontSize: "1.25rem",
                  padding: "0.25rem 0.5rem",
                  cursor: "pointer",
                }}
                aria-label="close"
              >
                ×
              </button>
            </div>
            <h2
              id="project-modal-title"
              style={{
                fontFamily: "var(--mono)",
                fontSize: "1.75rem",
                margin: "0 0 1rem 0",
                color: "var(--fg)",
                letterSpacing: "-0.01em",
              }}
            >
              {project.name}
            </h2>
            <p
              style={{
                fontFamily: "var(--sans)",
                fontSize: "0.9375rem",
                lineHeight: 1.65,
                color: "var(--fg)",
                margin: "0 0 1.5rem 0",
              }}
            >
              {project.blurb}
            </p>
            {project.tags.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.4rem",
                  marginBottom: "1.5rem",
                }}
              >
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: "0.7rem",
                      color: "var(--fg-dim)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      padding: "0.2rem 0.5rem",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
              <Link
                to={`/projects/${project.id}`}
                style={{
                  display: "inline-block",
                  fontFamily: "var(--mono)",
                  fontSize: "0.875rem",
                  color: "var(--accent)",
                  border: "1px solid var(--accent)",
                  padding: "0.6rem 1rem",
                  textDecoration: "none",
                }}
              >
                learn more →
              </Link>
              {project.url && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    fontFamily: "var(--mono)",
                    fontSize: "0.875rem",
                    color: "var(--fg-dim)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    padding: "0.6rem 1rem",
                    textDecoration: "none",
                  }}
                >
                  visit →
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
